import React, { useMemo, useState } from "react";
import "./StatisticsTab.css";
import { useChapterStats } from "./useChapterStats";
import { isManualAward } from "../../../helpers/manualAward";
import KPITiles from "./components/KPITiles";
import ApplicationsOverTime from "./components/ApplicationsOverTime";
import CategoryBreakdown from "./components/CategoryBreakdown";
import CategoriesOverTime from "./components/CategoriesOverTime";
import CategoryByDemographics from "./components/CategoryByDemographics";
import CategoryByChapter from "./components/CategoryByChapter";
import SelfIdBreakdown from "./components/SelfIdBreakdown";
import ReferralSources from "./components/ReferralSources";
import GeographicReach from "./components/GeographicReach";
import ReviewerEngagement from "./components/ReviewerEngagement";
import PipelineHealth from "./components/PipelineHealth";

// Chapter Statistics tab.
//
// Reads the caller's role off `user`:
//   • superAdmin              → chapter filter dropdown (All or a specific chapter)
//   • chapter_director / lp   → scoped to user.chapter (no toggle shown)
//
// Data loads via useChapterStats. Intermediate aggregations live in
// statsAggregations.js and render through small display components so the
// container stays composition-only.
export default function StatisticsTab({ user, chaptersFromPortal }) {
  const isSuperAdmin = user?.role === "superAdmin";
  const userChapter = user?.chapter || null;

  // For supers, state holds the selected filter. An empty string means
  // "All chapters" (aggregated view). Non-supers never read this state —
  // effectiveChapter comes from user.chapter in the hook.
  const [superFilter, setSuperFilter] = useState("");

  const chapterFilter = isSuperAdmin ? (superFilter || null) : userChapter;

  const stats = useChapterStats({
    user,
    chapterFilter: isSuperAdmin ? superFilter || null : undefined,
    chaptersFromPortal,
  });

  const scopeLabel = useMemo(() => {
    if (isSuperAdmin) return superFilter ? superFilter : "All chapters";
    return userChapter || "Your chapter";
  }, [isSuperAdmin, superFilter, userChapter]);

  const chapterOptions = useMemo(() => {
    return stats.chapters
      .filter((c) => c && c.name)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [stats.chapters]);

  // Application-pipeline panels (over-time, categories, self-ID, referrals,
  // geography, pipeline health) analyze the applicant pool, so manually
  // logged grant awards are excluded. KPITiles gets the full set: its
  // grants-funded and dollars-deployed tiles are the chapter's real totals,
  // manual awards included.
  const applicationPitches = useMemo(
    () => stats.pitches.filter((p) => !isManualAward(p)),
    [stats.pitches]
  );

  // Non-super with no chapter assignment — show a benign explanation rather
  // than a spinner that never resolves.
  if (!isSuperAdmin && !userChapter) {
    return (
      <div className="lp-statistics-tab lp-statistics-print-root">
        <PaneHeader scopeLabel="No chapter assigned" />
        <ScopeBar
          scopeLabel="No chapter assigned"
          isSuperAdmin={false}
          chapterOptions={[]}
          superFilter=""
          onFilterChange={() => {}}
          onPrint={() => {}}
        />
        <div className="lp-statistics-tab__body">
          <div style={{
            padding: 24,
            textAlign: "center",
            color: "var(--mb-ink-60)",
            fontSize: 13,
            border: "2px solid var(--mb-ink)",
            background: "var(--mb-paper)",
          }}>
            Your account doesn't have a chapter set yet. Ask an admin to assign you to a chapter,
            then come back.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lp-statistics-tab lp-statistics-print-root">
      <PaneHeader scopeLabel={scopeLabel} />
      <ScopeBar
        scopeLabel={scopeLabel}
        isSuperAdmin={isSuperAdmin}
        chapterOptions={chapterOptions}
        superFilter={superFilter}
        onFilterChange={setSuperFilter}
        onPrint={() => window.print()}
        loading={stats.loading}
      />

      <div className="lp-statistics-tab__body">
        {stats.error && (
          <div style={{
            padding: 12,
            marginBottom: 16,
            border: "2px solid var(--mb-ink)",
            background: "var(--mb-chalk)",
            color: "var(--mb-magenta-deep)",
            fontSize: 13,
          }}>
            Couldn't load statistics: {stats.error.message || String(stats.error)}
          </div>
        )}

        {stats.loading ? (
          <LoadingState />
        ) : (
          <>
            <KPITiles
              pitches={stats.pitches}
              chaptersByName={stats.chaptersByName}
              scopeLabel={scopeLabel}
            />
            <ApplicationsOverTime pitches={applicationPitches} />
            <CategoryBreakdown
              pitches={applicationPitches}
              chaptersByName={stats.chaptersByName}
            />
            <CategoriesOverTime pitches={applicationPitches} />
            <SelfIdBreakdown pitches={applicationPitches} chaptersByName={stats.chaptersByName} />
            <CategoryByDemographics pitches={applicationPitches} />
            {/* Cross-chapter heatmap is only meaningful when the loaded set spans
                multiple chapters — i.e. super-admin viewing "All chapters". For
                any single-chapter scope the row collapses and the chart is just
                a re-do of CategoryBreakdown. */}
            {isSuperAdmin && !chapterFilter && (
              <CategoryByChapter pitches={applicationPitches} />
            )}
            <ReferralSources pitches={applicationPitches} />
            <GeographicReach pitches={applicationPitches} />
            <PipelineHealth pitches={applicationPitches} />
            <ReviewerEngagement
              reviews={stats.reviews}
              users={stats.users}
              chapterFilter={chapterFilter}
            />
          </>
        )}
      </div>
    </div>
  );
}

// Chrome band above the report — scope context line + Archivo title.
function PaneHeader({ scopeLabel }) {
  return (
    <div className="win95-pane-header">
      <div>
        <div className="win95-pane-header__context">{scopeLabel}</div>
        <h2 className="win95-pane-header__title">Chapter statistics</h2>
      </div>
    </div>
  );
}

// Scope toolbar — chapter filter (supers only) + print, as OS chrome.
function ScopeBar({ scopeLabel, isSuperAdmin, chapterOptions, superFilter, onFilterChange, onPrint, loading }) {
  return (
    <div className="win95-toolbar" role="group" aria-label="Statistics scope" style={{ flexWrap: "wrap" }}>
      <span style={{
        fontFamily: "var(--font-chrome)",
        fontSize: "var(--tc-label)",
        fontWeight: 700,
        letterSpacing: "var(--track-label)",
        color: "var(--chrome-text-soft)",
      }}>
        Chapter scope
      </span>
      {isSuperAdmin ? (
        <div className="win95-select">
          <select
            className="win95-select__native"
            aria-label="Chapter filter"
            value={superFilter}
            onChange={(e) => onFilterChange(e.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="">All chapters</option>
            {chapterOptions.map((c) => (
              <option key={c.id || c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
          <span className="win95-select__arrow" aria-hidden="true" />
        </div>
      ) : (
        <span style={{
          fontFamily: "var(--font-chrome)",
          fontSize: "var(--tc-ui)",
          fontWeight: 700,
          color: "var(--chrome-outline)",
        }}>
          {scopeLabel}
        </span>
      )}
      {loading && (
        <span style={{ fontFamily: "var(--font-chrome)", fontSize: 11, color: "var(--chrome-text-soft)" }}>
          Loading…
        </span>
      )}
      <div className="win95-toolbar__spacer" />
      <button
        type="button"
        className="win95-btn win95-btn--compact"
        onClick={onPrint}
      >
        Print / save report
      </button>
    </div>
  );
}

function LoadingState() {
  return (
    <div style={{
      padding: 60,
      textAlign: "center",
      fontSize: 14,
      color: "var(--mb-ink-60)",
      border: "2px solid var(--mb-ink)",
      boxShadow: "var(--shadow-hard-sm)",
      background: "var(--mb-paper)",
    }}>
      Loading chapter statistics…
    </div>
  );
}
